import { UserType } from "@models/auth";
import { FeesDisplayStyle } from "../../enums/fee/displayStyle";
import { FeeHeatmapMode } from "../../enums/fee/heatmapMode";
import { FeeMissingInvestmentsOption } from "../../enums/fee/missingInvestments";
import { AnalysisStepBusinessMetricDTO } from "./businessMetric";
import { AnalysisCommentDTO } from "../comment";
import { AnalysisFeatureIncludeDTO, AnalysisFeatureDTO } from "./feature";
import { AnalysisFeeDTO } from "./fee";
import { FeePortfolioDetailDTO } from "../portfolioDetails";
import { SelectedProductDTO } from "../selectedProduct";
import { AnalysisUserType, HiddenSubProductDTO } from "../analysis";
import { TableViewMode } from "@models/platform-analysis/enums/tableViewMode";
import { FeeInvestmentMenuOption } from "@models/platform-analysis/enums/fee/investmentMenu";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { ManagedAccountFeeAnalysisContainerDTO } from "@models/managed-accounts/entities/step/fee";
import { FeesDisplayMode } from "@models/platform-analysis/enums/fee/displayMode";
import { AnalysisSummarySectionId } from "@models/platform-analysis/enums/summary/sectionId";
import { BooleanKeys } from "@models/core";
import { ProductRecommendationTypes } from "@models/platform-analysis/enums/summary/recommendationType";

export class AnalysisSummaryDTO {
  name = "";
  ownerId = "";
  ownerName = "";
  ownerType: UserType = UserType.SupplierPremiumOwner;
  ownerAvatarUrl: string | null = null;
  ownerARN: string | null = null;
  supplierId: string | null = null;
  supplierName: string | null = null;
  adviserName: string | null = null;
  practiceName: string | null = null;
  objectives: string | null = null;
  serial: string | null = null;
  concludingRemarks: string | null = null;
  firmId: string | null = null;
  firmAFSL: string | null = null;
  firmLogo: string | null = null;
  firmName: string | null = null;

  hasFeatureStep = false;
  hasBusinessMetricStep = false;
  hasFeeStep = false;
  lastModifiedDate: Date | null = null;
  featureAnalysis: AnalysisFeatureDTO | null = null;
  businessMetricSelectedFeatures: AnalysisFeatureIncludeDTO[] | null = null;
  businessMetricAnalysis: AnalysisStepBusinessMetricDTO | null = null;
  feeAnalysis: AnalysisFeeDTO | null = null;
  feePortfolioDetails: FeePortfolioDetailDTO | null = null;
  feesDisplayStyle: FeesDisplayStyle = FeesDisplayStyle.Dollar;
  feeMissingInvestmentsStyle: FeeMissingInvestmentsOption =
    FeeMissingInvestmentsOption.AllPlatform;
  investmentMenu: FeeInvestmentMenuOption = FeeInvestmentMenuOption.AllPlatform;
  feeTableViewMode: FeeHeatmapMode = FeeHeatmapMode.Projection;
  featureAnalysisTableViewMode: TableViewMode = TableViewMode.Default;
  analysisComments: AnalysisCommentDTO[] | null = null;
  selectedProducts: SelectedProductDTO[] | null = null;
  hiddenSubProducts: HiddenSubProductDTO[] | null = null;
  familyGroupName: string | null = null;
  familyMembersNames: string[] | null = null;
  selectedFeatures: AnalysisFeatureIncludeDTO[] | null = null;
  showSubSection = true;
  showComments = true;
  showSelectedFeatures = true;
  showAnalysisScore = true;
  hideFeeAnalysisGraph = false;
  portfolioSetup?: FeeCalculatorPortfolioLevel;
  smaPortfolioFee?: ManagedAccountFeeAnalysisContainerDTO;
  disqualifiedSmaPortfolioFee?: ManagedAccountFeeAnalysisContainerDTO;
  smaTableViewMode?: FeesDisplayMode;
  excludePerformanceInSMATable?: boolean;
  hidePortfolioCompositionSubtotals = false;
  showAppendixFeatureAnalysis: boolean | null = null;
  showAppendixBusinessMetricAnalysis: boolean | null = null;
  showAppendixFeeAnalysis: boolean | null = null;
  appendixFeeMissingInvestmentsStyle: FeeMissingInvestmentsOption | null = null;
  appendixInvestmentMenu: FeeInvestmentMenuOption | null = null;
  portfolioCompositionByPlatform?: PortfolioCompositionDTO;
  productRecommendations?: ProductRecommendationDTO[];
  disqualifiedPortfolioCompositionByPlatform?: PortfolioCompositionDTO;
  specificCommentSetting?: SummaryCommentSettingDto;
}

export class SummaryCommentSettingDto {
  showFeature = false;
  showBusinessMetric = false;
  showFee = false;
  showSummary = false;
}

export class PortfolioCompositionDTO {
  portfolioSize = 0;
  header = "";
  setups: PortfolioCompositionSetupDTO[] = [];
  sections: PortfolioCompositionSectionDTO[] = [];
  platformCodes: PortfolioCompositionPlatformCodeDTO[] = [];
}

export class PortfolioCompositionSetupDTO {
  id = "";
  name = "";
  setupType = "";
  isLatest?: boolean = false;
  outdatedAt: Date | null = null;
  productName = "";
  logo = "";
  isProductMain?: boolean = false;
  order = 0;
  rateCardVersion?: number = 0;

  // internal use only
  productId?: string = "";
  recommendation?: ProductRecommendationDTO;
}

export class PortfolioCompositionSectionDTO {
  isFeeCalculatorSection?: boolean = false;
  order = 0;
  rows: PortfolioCompositionRowDTO[] = [];
}

export class PortfolioCompositionRowDTO {
  id = "";
  name = "";
  isFeeCalculator?: boolean = false;
  isSubTotal?: boolean = false;
  isSingleRow?: boolean = false;
  order = 0;
  cells: PortfolioCompositionCellDTO[] = [];
}

export class PortfolioCompositionCellDTO {
  setupId = "";
  value = "";
  valueInPercent = "";
  order = 0;
  belowMinimumInvestmentSize?: boolean = false;
  rateCardVersion?: number = 0;

  // internal use only
  productId?: string = "";
}

export class PortfolioCompositionPlatformCodeDTO {
  shCode = "";
  name = "";
  order = 0;
  cells: PortfolioCompositionPlatformCodeCellDTO[] = [];
}

export class PortfolioCompositionPlatformCodeCellDTO {
  id = "";
  platformCode = "";
  order = 0;
}

export class ProductRecommendationDTO {
  productId = "";
  productName = "";
  productLogo = "";
  order = 0;
  isProductMain = false;
  recommendationType: ProductRecommendationTypes | null = null;
  additionalCustomLabel: string | null = null;
}

export interface RenderAnalysisSummarySectionProps {
  id: AnalysisSummarySectionId;
  name?: { [key in AnalysisUserType]?: string };
  showFor: AnalysisUserType[];
  Component: React.MemoExoticComponent<
    (props: RenderAnalysisSummaryItemProps) => JSX.Element
  > | null;
  dependencyField?: BooleanKeys<AnalysisSummaryDTO>;
  disabled?: boolean;
}

export interface RenderAnalysisSummaryItemProps
  extends Omit<RenderAnalysisSummarySectionProps, "Component"> {
  userType: AnalysisUserType;
  isReview: boolean;
  portfolioLevel?: FeeCalculatorPortfolioLevel;
  isReadOnly?: boolean;
}
